import React from 'react';
import { MailIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
export const NewsletterSection: React.FC = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const newsletterTitle = isAfterTide ? 'The Midnight Dispatch' : 'Stay in the Loop';
  const newsletterDescription = isAfterTide ? 'Join our newsletter for exclusive cover reveals, new releases, and curated recommendations from the darker side of fiction.' : 'Subscribe to our newsletter for new releases, trope alerts, exclusive merch drops, and upcoming events from your favorite indie authors.';
  const iconAccent = isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500';
  return <section className={`py-12 ${dk.surface}`} data-id="element-1819" data-mp="q0fggpby8dgs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1820" data-mp="l4qeyggdcmhu">
        <div className={`rounded-2xl shadow-md overflow-hidden border relative ${dk.card}`} data-id="element-1821" data-mp="2qze96ggbqk39">
          {/* Decorative top border */}
          <div className={`absolute top-0 left-0 right-0 h-1.5 ${isAfterTide ? 'bg-gradient-to-r from-[#e2b04a] to-[#c77dba]' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`} data-id="element-1822" data-mp="2vyzhvilaq6t6"></div>

          <div className="p-8 md:p-12 text-center" data-id="element-1823" data-mp="35xyge8cckm2g">
            <div className="flex justify-center mb-4" data-id="element-1824" data-mp="39ys5v65fu8xg">
              <div className={`p-3 rounded-full ${isAfterTide ? 'bg-[#e2b04a]/10' : 'bg-indigo-50'}`} data-id="element-1825" data-mp="39w5zh9ua9zgt">
                <MailIcon className={`h-8 w-8 ${iconAccent}`} data-id="element-1826" data-mp="3jobl81txzknk" />
              </div>
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold mb-3 ${dk.textHeading}`} data-id="element-1827" data-mp="3onoaet8ad3tb">
              {newsletterTitle}
            </h2>
            <p className={`mb-6 max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-1828" data-mp="3tk4yjnfhji6e">
              {newsletterDescription}
            </p>

            {/* Social Proof & Previews */}
            <div className="flex flex-col items-center mb-8" data-id="element-1829" data-mp="1yjutvf7uomn1">
              <div className="flex -space-x-2 overflow-hidden mb-3" data-id="element-1830" data-mp="1tk8b6q996e6u">
                {[1, 2, 3, 4].map(i => <img key={i} className={`inline-block h-8 w-8 rounded-full ring-2 ${dk.isDark ? 'ring-gray-800' : 'ring-white'}`} src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="" data-id="element-1831" data-mp="27bmwx4ujothl" />)}
              </div>
              <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-1832" data-mp="2c9y7xiwkta1e">
                {isAfterTide ? 'Join 3,000+ dark fiction readers' : 'Join 12,000+ readers'}
              </p>
              <div className={`flex flex-wrap justify-center gap-4 mt-4 text-xs font-medium ${dk.textMuted}`} data-id="element-1833" data-mp="2m9f9bo4sp1rk">
                {isAfterTide ? <>
                    <span className="flex items-center" data-id="element-1834" data-mp="rwfsxsslc3j8">
                      <span className="mr-1.5" data-id="element-1835" data-mp="hqfxlb1a4fep">🌙</span> Dark fiction picks
                    </span>
                    <span className="flex items-center" data-id="element-1836" data-mp="lr84hxmoi87t">
                      <span className="mr-1.5" data-id="element-1837" data-mp="lu46bx1ndh1j">⚡</span> Tier alerts
                    </span>
                    <span className="flex items-center" data-id="element-1838" data-mp="7z1zn7oz7nno">
                      <span className="mr-1.5" data-id="element-1839" data-mp="cyod9i8pv5x0">🔮</span> Author spotlights
                    </span>
                  </> : <>
                    <span className="flex items-center" data-id="element-1840" data-mp="31jm1400kk9c">
                      <span className="mr-1.5" data-id="element-1841" data-mp="3337d90j1lhz">📚</span> New release alerts
                    </span>
                    <span className="flex items-center" data-id="element-1842" data-mp="3l997nduumm44">
                      <span className="mr-1.5" data-id="element-1843" data-mp="3q5pve6y4yygo">🏷️</span> Trope recommendations
                    </span>
                    <span className="flex items-center" data-id="element-1844" data-mp="1jh34ydbwgz22">
                      <span className="mr-1.5" data-id="element-1845" data-mp="1of4kioa4ag8s">🎁</span> Exclusive merch drops
                    </span>
                  </>}
              </div>
            </div>

            <form className="max-w-md mx-auto" data-id="element-1846" data-mp="3kg6nhobq3bg7">
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-1847" data-mp="3fiwphka9prgr">
                <input type="email" placeholder="Your email address" className={`flex-grow px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 ${isAfterTide ? 'focus:ring-[#e2b04a]' : 'focus:ring-indigo-500'} ${dk.input}`} required data-id="element-1848" data-mp="3fhd63ktbf0ar" />
                <button type="submit" className={`font-medium px-6 py-3 rounded-lg transition-colors ${rt.primaryButton}`} data-id="element-1849" data-mp="2ws6q45jwomsn">
                  Subscribe
                </button>
              </div>
              <p className={`text-xs mt-3 ${dk.textFaint}`} data-id="element-1850" data-mp="2mwbw6ea8f5ak">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>;
};